import { Section } from '@/types';
import { ResponsiveBackgroundImage } from '@/types/sections';
import { getMediaTypeFromUrl } from './video-utils';

// Helper to safely get poster from cache (works on both server and client)
// On server, cache is not available, so we return null
const getCachedPoster = (url: string): string | null => {
  if (typeof window === 'undefined') {
    // Server-side: cache is not available
    return null;
  }
  
  try {
    // Dynamically import cache only on client-side
    // Use require to avoid bundling issues
    const { videoPosterCache } = require('./video-poster-cache');
    return videoPosterCache?.getPoster?.(url) || null;
  } catch {
    // If cache is not available, return null
    return null;
  }
};

export interface FallbackImage {
  src: string;
  alt: string;
}

/**
 * Extract fallback images from section data for video background fallback
 * 
 * Priority order:
 * 1. backgroundImage (primary fallback)
 * 2. videoThumbnail (secondary fallback)
 * 3. image (tertiary fallback)
 * 4. images array (additional fallbacks)
 */
export function extractFallbackImagesFromSection(
  section: Section,
  isMobile: boolean = false
): FallbackImage[] {
  const fallbackImages: FallbackImage[] = [];

  if (!section?.sectionData) {
    return fallbackImages;
  }

  const sectionData = section.sectionData;

  // Helper to add image if valid (and not a video)
  const addImage = (url: string | undefined, alt: string = 'Background') => {
    if (url && typeof url === 'string' && url.trim()) {
      // Normalize /api/v1/media/download to /api/media/download
      const normalized = url.includes('/api/v1/media/download')
        ? url.replace('/api/v1/media/download', '/api/media/download')
        : url;
      
      // Skip video URLs - only include actual images
      const mediaType = getMediaTypeFromUrl(normalized);
      if (mediaType === 'video') {
        return; // Skip videos - they're not fallback images
      }
      
      // Avoid duplicates
      if (!fallbackImages.some(img => img.src === normalized)) {
        fallbackImages.push({ src: normalized, alt });
      }
    }
  };

  // Helper to extract from ResponsiveBackgroundImage
  const extractFromResponsive = (bg: ResponsiveBackgroundImage | string | undefined, alt: string = 'Background') => {
    if (!bg) return;

    if (typeof bg === 'string') {
      addImage(bg, alt);
    } else if (typeof bg === 'object') {
      // In mobile, prioritize mobile images first (usually smaller/optimized)
      // In desktop, prioritize desktop images first (usually higher quality)
      if (isMobile) {
        // Mobile: prefer mobile first, then desktop as fallback
        if (bg.mobile) addImage(bg.mobile, alt);
        // Only add desktop if mobile doesn't exist (to avoid loading large images on mobile)
        if (!bg.mobile && bg.desktop) addImage(bg.desktop, alt);
      } else {
        // Desktop: prefer desktop first, then mobile as fallback
        if (bg.desktop) addImage(bg.desktop, alt);
        if (bg.mobile) addImage(bg.mobile, alt);
      }
    }
  };

  // 0. Highest priority fallback: poster (video poster image)
  if (sectionData.poster && typeof sectionData.poster === 'string') {
    addImage(sectionData.poster, 'Video poster');
  }

  // 0.5. High priority fallback: backgroundMediaFallback (extracted from video frame)
  // This is the most important fallback for sections with videos
  // Can be string or responsive object (mobile/desktop)
  // If it's a video URL, check if we have a cached poster for it
  
  // 0.5a. Highest priority on mobile: backgroundMediaFallbackMobile (mobile-specific fallback)
  // This takes precedence over backgroundMediaFallback on mobile devices
  if (isMobile && sectionData.backgroundMediaFallbackMobile) {
    if (typeof sectionData.backgroundMediaFallbackMobile === 'string') {
      const mediaType = getMediaTypeFromUrl(sectionData.backgroundMediaFallbackMobile);
      if (mediaType === 'video') {
        const cachedPoster = getCachedPoster(sectionData.backgroundMediaFallbackMobile);
        if (cachedPoster) {
          addImage(cachedPoster, 'Cached video poster (mobile fallback)');
        }
      } else {
        addImage(sectionData.backgroundMediaFallbackMobile, 'Video frame fallback (mobile)');
      }
    }
  }
  
  // 0.5b. Desktop fallback: backgroundMediaFallback (desktop fallback)
  // On mobile, this is only used if backgroundMediaFallbackMobile doesn't exist
  if (sectionData.backgroundMediaFallback) {
    if (typeof sectionData.backgroundMediaFallback === 'string') {
      // On mobile, only use desktop fallback if mobile fallback doesn't exist
      if (isMobile && sectionData.backgroundMediaFallbackMobile) {
        // Skip desktop fallback on mobile if mobile fallback exists
      } else {
        const mediaType = getMediaTypeFromUrl(sectionData.backgroundMediaFallback);
        if (mediaType === 'video') {
          // Check if we have a cached poster for this video
          const cachedPoster = getCachedPoster(sectionData.backgroundMediaFallback);
          if (cachedPoster) {
            addImage(cachedPoster, 'Cached video poster');
          }
        } else {
          addImage(sectionData.backgroundMediaFallback, 'Video frame fallback');
        }
      }
    } else if (typeof sectionData.backgroundMediaFallback === 'object' && sectionData.backgroundMediaFallback !== null) {
      // Handle responsive backgroundMediaFallback
      const responsiveFallback = sectionData.backgroundMediaFallback as ResponsiveBackgroundImage;
      // In mobile, prioritize mobile version first (but only if backgroundMediaFallbackMobile doesn't exist)
      if (isMobile) {
        // If we have a dedicated mobile fallback, skip responsive fallback
        if (!sectionData.backgroundMediaFallbackMobile) {
          if (responsiveFallback.mobile) {
            const mediaType = getMediaTypeFromUrl(responsiveFallback.mobile);
            if (mediaType === 'video') {
              const cachedPoster = getCachedPoster(responsiveFallback.mobile);
              if (cachedPoster) {
                addImage(cachedPoster, 'Cached video poster (mobile)');
              }
            } else {
              addImage(responsiveFallback.mobile, 'Video frame fallback (mobile)');
            }
          }
          // Only use desktop fallback if mobile doesn't exist
          if (!responsiveFallback.mobile && responsiveFallback.desktop) {
            const mediaType = getMediaTypeFromUrl(responsiveFallback.desktop);
            if (mediaType === 'video') {
              const cachedPoster = getCachedPoster(responsiveFallback.desktop);
              if (cachedPoster) {
                addImage(cachedPoster, 'Cached video poster (desktop fallback)');
              }
            } else {
              addImage(responsiveFallback.desktop, 'Video frame fallback (desktop fallback)');
            }
          }
        }
      } else {
        // Desktop: prefer desktop, then mobile
        if (responsiveFallback.desktop) {
          const mediaType = getMediaTypeFromUrl(responsiveFallback.desktop);
          if (mediaType === 'video') {
            const cachedPoster = getCachedPoster(responsiveFallback.desktop);
            if (cachedPoster) {
              addImage(cachedPoster, 'Cached video poster (desktop)');
            }
          } else {
            addImage(responsiveFallback.desktop, 'Video frame fallback (desktop)');
          }
        }
        if (responsiveFallback.mobile) {
          const mediaType = getMediaTypeFromUrl(responsiveFallback.mobile);
          if (mediaType === 'video') {
            const cachedPoster = getCachedPoster(responsiveFallback.mobile);
            if (cachedPoster) {
              addImage(cachedPoster, 'Cached video poster (mobile)');
            }
          } else {
            addImage(responsiveFallback.mobile, 'Video frame fallback (mobile)');
          }
        }
      }
    }
  }
  
  // 0.6. Check backgroundVideo for cached posters
  if (sectionData.backgroundVideo) {
    let videoUrl: string | undefined;
    
    if (typeof sectionData.backgroundVideo === 'string') {
      videoUrl = sectionData.backgroundVideo;
    } else if (typeof sectionData.backgroundVideo === 'object' && sectionData.backgroundVideo !== null) {
      const responsiveVideo = sectionData.backgroundVideo as ResponsiveBackgroundImage;
      videoUrl = isMobile 
        ? (responsiveVideo.mobile || responsiveVideo.desktop)
        : (responsiveVideo.desktop || responsiveVideo.mobile);
    }
    
    if (videoUrl && typeof videoUrl === 'string') {
      const cachedPoster = getCachedPoster(videoUrl);
      if (cachedPoster) {
        addImage(cachedPoster, 'Cached video poster');
      }
    }
  }

  // 1. Primary fallback: backgroundImage
  extractFromResponsive(
    sectionData.backgroundImage as ResponsiveBackgroundImage | string | undefined,
    'Background image'
  );

  // 2. Secondary fallback: videoThumbnail
  if (sectionData.videoThumbnail && typeof sectionData.videoThumbnail === 'string') {
    addImage(sectionData.videoThumbnail, 'Video thumbnail');
  }

  // 3. Tertiary fallback: image
  if (sectionData.image && typeof sectionData.image === 'string') {
    addImage(sectionData.image, 'Section image');
  }

  // 4. Additional fallbacks: images array
  if (Array.isArray(sectionData.images)) {
    sectionData.images.forEach((img, index) => {
      if (typeof img === 'string') {
        addImage(img, `Fallback image ${index + 1}`);
      }
    });
  }

  return fallbackImages;
}

/**
 * Extract fallback images from section props (for component usage)
 */
export function extractFallbackImagesFromProps(props: {
  backgroundImage?: ResponsiveBackgroundImage | string;
  videoThumbnail?: string;
  image?: string;
  images?: string[];
}, isMobile: boolean = false): FallbackImage[] {
  const fallbackImages: FallbackImage[] = [];

  // Helper to add image if valid (and not a video)
  const addImage = (url: string | undefined, alt: string = 'Background') => {
    if (url && typeof url === 'string' && url.trim()) {
      const normalized = url.includes('/api/v1/media/download')
        ? url.replace('/api/v1/media/download', '/api/media/download')
        : url;
      
      // Skip video URLs - only include actual images
      const mediaType = getMediaTypeFromUrl(normalized);
      if (mediaType === 'video') {
        return; // Skip videos - they're not fallback images
      }
      
      if (!fallbackImages.some(img => img.src === normalized)) {
        fallbackImages.push({ src: normalized, alt });
      }
    }
  };

  // Helper to extract from ResponsiveBackgroundImage
  const extractFromResponsive = (bg: ResponsiveBackgroundImage | string | undefined, alt: string = 'Background') => {
    if (!bg) return;

    if (typeof bg === 'string') {
      addImage(bg, alt);
    } else if (typeof bg === 'object') {
      if (isMobile) {
        if (bg.mobile) addImage(bg.mobile, alt);
        if (bg.desktop) addImage(bg.desktop, alt);
      } else {
        if (bg.desktop) addImage(bg.desktop, alt);
        if (bg.mobile) addImage(bg.mobile, alt);
      }
    }
  };

  // Priority order: backgroundImage, videoThumbnail, image, images
  extractFromResponsive(props.backgroundImage, 'Background image');
  
  if (props.videoThumbnail) {
    addImage(props.videoThumbnail, 'Video thumbnail');
  }

  if (props.image) {
    addImage(props.image, 'Section image');
  }

  if (Array.isArray(props.images)) {
    props.images.forEach((img, index) => {
      if (typeof img === 'string') {
        addImage(img, `Fallback image ${index + 1}`);
      }
    });
  }

  return fallbackImages;
}

/**
 * Get default fallback images if none are available from section
 * Returns empty array - let components handle defaults
 */
export function getDefaultFallbackImages(): FallbackImage[] {
  // Return empty - components can provide their own defaults
  return [];
}

